import { useState, useEffect, useCallback, useRef } from 'react';
import { useDeviceService } from './useDeviceService';

/**
 * Specialized hook for monitoring device online status changes
 * Provides real-time updates when devices go online/offline
 */
export const useDeviceOnlineStatus = () => {
  const { devices, subscribeToDeviceOnlineStatus } = useDeviceService();
  const [onlineStatusChanges, setOnlineStatusChanges] = useState([]);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const unsubscribeRef = useRef(null);

  /**
   * Start monitoring device online status changes
   */
  const startMonitoring = useCallback(() => {
    if (isMonitoring || unsubscribeRef.current) return;

    setIsMonitoring(true);
    
    unsubscribeRef.current = subscribeToDeviceOnlineStatus((updatedDevices, changes) => {
      console.log('🔍 useDeviceOnlineStatus - Received changes:', changes.length, 'changes');
      console.log('📱 Updated devices:', updatedDevices.length, 'devices');
      
      const newChanges = changes.map(change => ({
        ...change,
        timestamp: Date.now(),
        device: updatedDevices.find(d => d.key === change.newDoc?.key)
      }));
      
      console.log('🔄 Processing changes:', newChanges.length, 'changes');
      setOnlineStatusChanges(prev => [...newChanges, ...prev].slice(0, 50)); // Keep last 50 changes

      // Log status changes (toast notifications are now handled globally)
      newChanges.forEach(change => {
        const device = change.device;
        if (device) {
          const status = device.isOnline ? '🟢 Online' : '🔴 Offline';
          const isTimerTriggered = change.triggeredBy === 'timer';
          
          console.log('🔔 Status change detected for device:', device.name, status, isTimerTriggered ? '(Timer triggered)' : '');
        }
      });
    });
  }, [subscribeToDeviceOnlineStatus, isMonitoring]);

  /**
   * Stop monitoring device online status changes
   */
  const stopMonitoring = useCallback(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    setIsMonitoring(false);
  }, []);

  /**
   * Clear online status change history
   */
  const clearStatusHistory = useCallback(() => {
    setOnlineStatusChanges([]);
  }, []);

  /**
   * Get devices that recently changed status
   */
  const getRecentStatusChanges = useCallback((minutes = 5) => {
    const cutoffTime = Date.now() - (minutes * 60 * 1000);
    return onlineStatusChanges.filter(change => change.timestamp > cutoffTime);
  }, [onlineStatusChanges]);

  /**
   * Get devices that went offline recently
   */
  const getRecentlyOfflineDevices = useCallback((minutes = 5) => {
    const cutoffTime = Date.now() - (minutes * 60 * 1000);
    return onlineStatusChanges
      .filter(change => 
        change.timestamp > cutoffTime && 
        change.device && 
        !change.device.isOnline
      )
      .map(change => change.device);
  }, [onlineStatusChanges]);

  /**
   * Get devices that came online recently
   */
  const getRecentlyOnlineDevices = useCallback((minutes = 5) => {
    const cutoffTime = Date.now() - (minutes * 60 * 1000);
    return onlineStatusChanges
      .filter(change => 
        change.timestamp > cutoffTime && 
        change.device && 
        change.device.isOnline
      )
      .map(change => change.device);
  }, [onlineStatusChanges]);

  /**
   * Get online status statistics
   */
  const getStatusStats = useCallback(() => {
    const now = Date.now();
    const lastHour = onlineStatusChanges.filter(change => 
      change.timestamp > now - (60 * 60 * 1000)
    );
    const last24Hours = onlineStatusChanges.filter(change => 
      change.timestamp > now - (24 * 60 * 60 * 1000)
    );

    return {
      totalChanges: onlineStatusChanges.length,
      changesLastHour: lastHour.length,
      changesLast24Hours: last24Hours.length,
      offlineEvents: onlineStatusChanges.filter(change => 
        change.device && !change.device.isOnline
      ).length,
      onlineEvents: onlineStatusChanges.filter(change => 
        change.device && change.device.isOnline
      ).length,
      currentOnlineCount: devices.filter(d => d.isOnline).length,
      currentOfflineCount: devices.filter(d => !d.isOnline).length
    };
  }, [onlineStatusChanges, devices]);

  // Auto-start monitoring when devices are available (only once)
  useEffect(() => {
    if (devices.length > 0 && !isMonitoring) {
      startMonitoring();
    }
  }, [devices.length]); // Remove isMonitoring and monitoring functions from dependencies

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopMonitoring();
    };
  }, [stopMonitoring]);

  return {
    // State
    onlineStatusChanges,
    isMonitoring,
    
    // Actions
    startMonitoring,
    stopMonitoring,
    clearStatusHistory,
    
    // Queries
    getRecentStatusChanges,
    getRecentlyOfflineDevices,
    getRecentlyOnlineDevices,
    getStatusStats,
    
    // Current device status
    currentDevices: devices,
    onlineDevices: devices.filter(d => d.isOnline),
    offlineDevices: devices.filter(d => !d.isOnline)
  };
};

export default useDeviceOnlineStatus; 